import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ChangeStatus, SaveStatus, ContentSegment } from '@/types/editor.types';

/**
 */
export interface EditorState {
  changeStatus: ChangeStatus;
  saveStatus: SaveStatus;
  lastSavedContent: string;
  lastSavedAt: number | null;
  error: Error | null;
}

/**
 */
export interface SaveQueueItem {
  topicNodeId: string;
  segments: ContentSegment[];
  timestamp: number;
  retryCount: number;
  lastError?: string;
}

/**
 */
interface EditorStoreState {
  editorStates: Record<string, EditorState>;

  saveQueue: Record<string, SaveQueueItem>;

  isServiceWorkerReady: boolean;
  isBackgroundSyncSupported: boolean;

  initEditorState: (topicNodeId: string, initialContent: string) => void;
  updateEditorState: (topicNodeId: string, updates: Partial<EditorState>) => void;
  setChangeStatus: (topicNodeId: string, status: ChangeStatus) => void;
  setSaveStatus: (topicNodeId: string, status: SaveStatus) => void;
  setLastSaved: (topicNodeId: string, content: string, timestamp: number) => void;
  setError: (topicNodeId: string, error: Error | null) => void;
  clearEditorState: (topicNodeId: string) => void;

  addToSaveQueue: (topicNodeId: string, segments: ContentSegment[]) => void;
  removeFromSaveQueue: (topicNodeId: string) => void;
  incrementRetry: (topicNodeId: string) => void;
  getSaveQueueItem: (topicNodeId: string) => SaveQueueItem | undefined;
  getAllPendingItems: () => SaveQueueItem[];

  // Actions: Service Worker
  setServiceWorkerReady: (ready: boolean) => void;
  setBackgroundSyncSupported: (supported: boolean) => void;
}

/**
 */
const DEFAULT_EDITOR_STATE: EditorState = {
  changeStatus: 'unchanged',
  saveStatus: 'idle',
  lastSavedContent: '',
  lastSavedAt: null,
  error: null,
};

/**
 * Zustand Store
 */
export const useEditorStore = create<EditorStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      editorStates: {},
      saveQueue: {},
      isServiceWorkerReady: false,
      isBackgroundSyncSupported: false,

      // ========================================
      // ========================================

      initEditorState: (topicNodeId, initialContent) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...DEFAULT_EDITOR_STATE,
              lastSavedContent: initialContent,
              lastSavedAt: Date.now(),
            },
          },
        }), false, 'initEditorState');
      },

      updateEditorState: (topicNodeId, updates) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...state.editorStates[topicNodeId],
              ...updates,
            },
          },
        }), false, 'updateEditorState');
      },

      setChangeStatus: (topicNodeId, status) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...(state.editorStates[topicNodeId] || DEFAULT_EDITOR_STATE),
              changeStatus: status,
            },
          },
        }), false, 'setChangeStatus');
      },

      setSaveStatus: (topicNodeId, status) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...(state.editorStates[topicNodeId] || DEFAULT_EDITOR_STATE),
              saveStatus: status,
            },
          },
        }), false, 'setSaveStatus');
      },

      setLastSaved: (topicNodeId, content, timestamp) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...(state.editorStates[topicNodeId] || DEFAULT_EDITOR_STATE),
              lastSavedContent: content,
              lastSavedAt: timestamp,
              changeStatus: 'unchanged',
            },
          },
        }), false, 'setLastSaved');
      },

      setError: (topicNodeId, error) => {
        set((state) => ({
          editorStates: {
            ...state.editorStates,
            [topicNodeId]: {
              ...(state.editorStates[topicNodeId] || DEFAULT_EDITOR_STATE),
              error,
              saveStatus: error ? 'error' : 'idle',
            },
          },
        }), false, 'setError');
      },

      clearEditorState: (topicNodeId) => {
        set((state) => {
          const { [topicNodeId]: _, ...rest } = state.editorStates;
          return { editorStates: rest };
        }, false, 'clearEditorState');
      },

      // ========================================
      // ========================================

      addToSaveQueue: (topicNodeId, segments) => {
        set((state) => ({
          saveQueue: {
            ...state.saveQueue,
            [topicNodeId]: {
              topicNodeId,
              segments,
              timestamp: Date.now(),
              retryCount: state.saveQueue[topicNodeId]?.retryCount || 0,
            },
          },
        }), false, 'addToSaveQueue');
      },

      removeFromSaveQueue: (topicNodeId) => {
        set((state) => {
          const { [topicNodeId]: _, ...rest } = state.saveQueue;
          return { saveQueue: rest };
        }, false, 'removeFromSaveQueue');
      },

      incrementRetry: (topicNodeId) => {
        set((state) => {
          const item = state.saveQueue[topicNodeId];
          if (!item) return state;

          return {
            saveQueue: {
              ...state.saveQueue,
              [topicNodeId]: {
                ...item,
                retryCount: item.retryCount + 1,
              },
            },
          };
        }, false, 'incrementRetry');
      },

      getSaveQueueItem: (topicNodeId) => {
        return get().saveQueue[topicNodeId];
      },

      getAllPendingItems: () => {
        return Object.values(get().saveQueue);
      },

      // ========================================
      // ========================================

      setServiceWorkerReady: (ready) => {
        set({ isServiceWorkerReady: ready }, false, 'setServiceWorkerReady');
      },

      setBackgroundSyncSupported: (supported) => {
        set({ isBackgroundSyncSupported: supported }, false, 'setBackgroundSyncSupported');
      },
    }),
    {
      name: 'Editor Store',
    }
  )
);
